import React, { useState } from 'react'

import DOMPurify from 'dompurify'

import format from '../../../services/format'
import { SUGGESTED_FIELD_LENGTH } from '../../../utils/Constants'
import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'
import TextArea from '../../controls/TextArea'

import 'react-datepicker/dist/react-datepicker.css'

export default {
  name: 'Injury',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  // No fields in the Injury section are required — descriptions are optional
  return validation.object({
    injury: validation
      .object({
        descriptions: validation
          .arrayOf(
            validation.object({
              description: validation.richText('Description of Injury').nullable(),
              provider: validation.shortText('Provided By').nullable(),
              date: validation.date('Date').nullable(),
            }),
          )
          .nullable(),
        notes: validation.richText('Injury Notes').nullable(),
      })
      .nullable(),
  })
}

function Component({
  caseModel,
  caseFiles,
  handleNewCaseFileAttached,
  createInputProps,
  getValue,
  addItemToArray,
  removeItemFromArray,
  ErrorMessage,
}) {
  const [editingIndex, setEditingIndex] = useState(null)
  const descriptions = getValue('injury.descriptions', [])

  const handleAddDescription = () => {
    const nextIndex = descriptions.length
    addItemToArray('injury.descriptions', { description: '', provider: '', date: null })
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    removeItemFromArray('injury.descriptions', index)
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div>
      <h4 className="pb-3">Injury</h4>
      <div className="mb-4">
        {descriptions.map((injury, index) => {
          const isEditing = editingIndex === index
          const descVal = getValue(`injury.descriptions[${index}].description`, injury?.description || '')
          const providerVal = getValue(`injury.descriptions[${index}].provider`, injury?.provider || '')
          const dateVal = getValue(`injury.descriptions[${index}].date`, injury?.date || '')
          const hasDescContent = Boolean(descVal && descVal.replace(/<[^>]*>/g, '').trim())
          const cleanDescHtml = hasDescContent
            ? descVal
                .trim()
                .replace(/<\/p>\s*<p[^>]*>/gi, '<br />')
                .replace(/^<p[^>]*>/i, '')
                .replace(/<\/p>$/i, '')
            : ''
          const formattedDate = dateVal ? format.date(dateVal) : null

          return (
            <React.Fragment key={`injury-item-${index}`}>
              {index > 0 ? <div className="mt-3" /> : null}
              {!isEditing ? (
                <div className="d-flex align-items-start justify-content-between clearfix">
                  <div className="w-100 mr-3">
                    <div>
                      {hasDescContent ? (
                        <span
                          className="font-weight-semibold"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanDescHtml) }}
                        />
                      ) : (
                        <span className="font-weight-semibold">
                          <em className="text-muted">Untitled description</em>
                        </span>
                      )}
                      {(providerVal || formattedDate) && (
                        <span className="text-muted ml-2" style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                          - {[providerVal, formattedDate].filter(Boolean).join(' (')}
                          {providerVal && formattedDate ? ')' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => setEditingIndex(index)}
                      title="Edit this injury description"
                    >
                      <i className="fas fa-pen" aria-hidden="true" />
                    </button>
                    <RemoveButton id={`remove-injury-${index}`} className="btn-sm" onClick={() => handleRemove(index)}>
                      Remove
                    </RemoveButton>
                  </div>
                </div>
              ) : (
                <div className="clearfix">
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <label className="form-label" htmlFor={`injury.descriptions[${index}].description`}>
                        Description of Injury
                      </label>
                      <TextArea
                        areaProps={{ ...createInputProps(`injury.descriptions[${index}].description`) }}
                        rows={4}
                        maxLength={validation.maxLengths.richText}
                      />
                      <ErrorMessage name={`injury.descriptions[${index}].description`} />
                    </div>
                    <div className="form-group col-md-3">
                      <label className="form-label" htmlFor={`injury.descriptions[${index}].provider`}>
                        Provided By
                      </label>
                      <input
                        type="text"
                        id={`injury.descriptions[${index}].provider`}
                        {...createInputProps(`injury.descriptions[${index}].provider`)}
                        className="form-control"
                        placeholder="Enter name and title..."
                        maxLength={SUGGESTED_FIELD_LENGTH}
                      />
                      <ErrorMessage name={`injury.descriptions[${index}].provider`} />
                    </div>
                    <div className="form-group col-md-3">
                      <label className="form-label" htmlFor={`injury.descriptions[${index}].date`}>
                        Date
                      </label>
                      <div className="d-flex">
                        <DatePicker
                          id={`injury.descriptions[${index}].date`}
                          {...createInputProps(`injury.descriptions[${index}].date`)}
                          placeholderText="Date"
                        />
                      </div>
                      <ErrorMessage name={`injury.descriptions[${index}].date`} />
                    </div>
                  </div>
                  <div className="form-row mb-2 mt-2">
                    <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setEditingIndex(null)}
                      >
                        Done
                      </button>
                      <RemoveButton
                        id={`remove-injury-${index}`}
                        className="btn-sm"
                        onClick={() => handleRemove(index)}
                      >
                        Remove
                      </RemoveButton>
                    </div>
                  </div>
                </div>
              )}
              <hr />
            </React.Fragment>
          )
        })}

        <AddButton id="InjuryDescriptionAddBtn" onClick={handleAddDescription}>
          Add Injury Description
        </AddButton>
      </div>

      <div className="form-row mt-4">
        <div className="col-md-8">
          <div className="form-group">
            <label className="form-label mt-2" htmlFor="injury.notes">
              Injury Notes
            </label>
            <TextArea areaProps={{ ...createInputProps('injury.notes') }} />
            <div style={{ display: 'inline' }}>
              <ErrorMessage name={'injury.notes'} />
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-3 px-0">
        <div className="form-group">
          <label className="form-label mt-2" htmlFor="injury.date">
            Date of Injury*
          </label>
          <DatePicker {...createInputProps('injury.date')} placeholderText="Date of Injury" />
          <ErrorMessage name="injury.date" />
        </div>
      </div>
    </div>
  )
}
